import { useMemo, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  selectClasses,
  Box,
} from '@mui/material';
import groupBy from 'just-group-by';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Search from '@mui/icons-material/Search';
import { IconX, IconPlus } from '@tabler/icons-react';
import { styled } from '@mui/material/styles';

import Chip from '../Chip';
import Typography from '../Typography';
import { ListOption, SelectDropdownProps } from './types';
import SearchBar from '../SearchBar';
import NonIdealState from '../NonIdealState';

import { MenuBody } from './MenuBody';
import { filterListOptionsWithParentsAndChildren } from './utils';

function SelectDropdown<T extends string | string[] = string>({
  disabled,
  error,
  id,
  label,
  options,
  placeholder,
  optional,
  value,
  isMenuSearchable,
  menuTitle,
  menuDescription,
  chipsToShow,
  onDeleteChip,
  onClear,
  onClickOnlyButton,
  startAdornment,
  menuMaxHeight,
  inputTypographyVariant,
  disablePortal,
  onAddNewClick,
  addNewButtonText = 'Add new',
  helperText,
  topMenuChildren,
  shouldErrorMessageTakeUpSpace = true,
  requiredLabel = 'Required',
  showRequiredLabel = false,
  wrapItems = false,
  ...rest
}: SelectDropdownProps<T>) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [searchText, setSearchText] = useState('');

  const selectedOption = useMemo<ListOption | undefined>(
    () =>
      typeof value === 'string'
        ? options.find(option => option.value === value)
        : undefined,
    [options, value],
  );
  const filteredOptions = useMemo(() => {
    return filterListOptionsWithParentsAndChildren(options, searchText);
  }, [searchText, options]);

  const filteredAndCategorized = groupBy(filteredOptions, ({ category }) =>
    String(category),
  );
  const hasCategories =
    Object.keys(groupBy(options, ({ category }) => String(category))).length >
    1;

  const inputTextStyle = inputTypographyVariant
    ? theme.typography[inputTypographyVariant]
    : theme.typography.font14;

  const renderOptionByCategory = (category: string) => {
    const categoryName =
      category === 'undefined' ? 'Not categorized' : category;
    const categoryList = filteredAndCategorized[category];
    const menuItems = categoryList.length
      ? categoryList.map(option => renderMenuItem(option))
      : [];
    const categoryElement = hasCategories ? (
      <MenuItem key={category} disabled>
        <Typography variant='font14' color={theme.palette.common.black}>
          {categoryName}
        </Typography>
      </MenuItem>
    ) : null;

    return [categoryElement, menuItems];
  };

  const renderMenuItem = (menuProps: ListOption) => {
    const isSelectedOption =
      typeof menuProps.value === 'string' &&
      menuProps.value === selectedOption?.value;
    return (
      <MenuItem
        disabled={menuProps.disabled}
        key={menuProps.value}
        sx={[
          {
            alignItems: 'center',
            backgroundColor: 'transparent',
            display: 'flex',
            justifyContent: 'space-between',
            paddingLeft: 2 + (menuProps.indent || 0) * 2,
            '&:hover': {
              backgroundColor: `${theme.palette.colors.purple[100]} !important`,
              '& .MuiButton-root': {
                display: 'flex',
              },
            },
            '&.Mui-selected': {
              backgroundColor: 'transparent',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '& .MuiButton-root': {
              display: 'none',
            },
          },
          Array.isArray(value) && {
            padding: '0 16px',
          },
        ]}
        value={menuProps.value}
      >
        <MenuBody
          inputValue={value}
          isSelectedOption={isSelectedOption}
          onClickOnlyButton={onClickOnlyButton}
          {...menuProps}
        />
      </MenuItem>
    );
  };

  const ClearButton = () => (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        position: 'absolute',
        top: '6px',
        right: '32px',
        display: 'none',
      }}
      className='show-on-hover'
    >
      <Box
        sx={{
          backgroundColor: theme.palette.colors.slate[100],
          borderRadius: '50%',
          padding: '4px',
          display: 'flex',
        }}
        onClick={onClear}
        onMouseDown={e => e.stopPropagation()}
      >
        <IconX color={theme.palette.colors.grey[700]} size={20} />
      </Box>
    </Box>
  );

  return (
    <FormControl error={!!error} fullWidth>
      {label && (
        <InputLabel
          id={id}
          sx={{
            ...theme.typography.font14Medium,
            color: theme.palette.common.black,
            position: 'static',
            transform: 'none',
            padding: `0 0 ${theme.spacing(1)}`,
            '&.Mui-focused': {
              color: theme.palette.common.black,
            },
            '&.Mui-error': {
              color: theme.palette.common.black,
            },
            display: 'flex',
            whiteSpace: 'initial',
          }}
        >
          {label}
          {label && showRequiredLabel && (
            <Box ml={0.5} display='flex' alignItems='center'>
              <Typography
                variant='font14'
                color={theme.palette.colors.grey[700]}
              >
                {requiredLabel}
              </Typography>
            </Box>
          )}
          {optional && (
            <Box ml={0.5} display='flex' alignItems='center'>
              <Typography
                variant='font14'
                color={theme.palette.colors.grey[700]}
              >
                Optional
              </Typography>
            </Box>
          )}
        </InputLabel>
      )}
      <Select
        disabled={disabled}
        displayEmpty
        error={!!error}
        fullWidth
        IconComponent={props => (
          <KeyboardArrowDown
            {...props}
            sx={{
              fill: disabled
                ? theme.palette.colors.grey[400]
                : isDarkMode
                  ? theme.palette.colors.white
                  : theme.palette.common.black,
            }}
          />
        )}
        labelId={id}
        MenuProps={{
          autoFocus: !isMenuSearchable,
          PaperProps: {
            elevation: 2,
            sx: {
              border: `1px solid ${theme.palette.colors.grey[500]}`,
              maxHeight: menuMaxHeight,
            },
          },
          transformOrigin: {
            vertical: -4,
            horizontal: 'center',
          },
          disablePortal: disablePortal,
          sx: {
            '& .MuiMenu-list': {
              ...(onAddNewClick && { paddingBottom: 0 }),
            },
            zIndex: 1301,
          },
        }}
        sx={[
          {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.colors.grey[500],
            },
            [`& .${selectClasses.select}`]: {
              ...inputTextStyle,
              borderColor: theme.palette.colors.grey[500],
              color: selectedOption
                ? selectedOption.color ||
                  (isDarkMode
                    ? theme.palette.colors.white
                    : theme.palette.colors.black)
                : theme.palette.colors.grey[600],
              padding:
                Array.isArray(value) && value.length ? '6px 16px' : '10px 16px',
            },
            [`& .${selectClasses.select}:hover`]: {
              borderColor: theme.palette.colors.grey[600],
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.colors.slate[100],
              color: theme.palette.colors.grey[400],
              '.MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.colors.grey[100],
              },
            },
          },
          isDarkMode && {
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.colors.purple[400],
            },
          },
          // controls placeholder when in multiple mode
          placeholder
            ? {
                '& .MuiSelect-select .notranslate::after': placeholder
                  ? {
                      content: `"${placeholder}"`,
                    }
                  : {},
              }
            : null,
        ]}
        value={value}
        renderValue={selected => {
          if (Array.isArray(selected)) {
            if (selected.length === 0) {
              return undefined;
            }
            return (
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                sx={{
                  '&:hover .show-on-hover': {
                    display: 'flex',
                  },
                  '&:hover .hide-on-hover': {
                    display: 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    width: '100%',
                    overflow: 'hidden',
                    flexWrap: wrapItems ? 'wrap' : undefined,
                  }}
                >
                  {selected.slice(0, chipsToShow).map((sliceValue: string) => (
                    <Chip
                      fontSize={14}
                      backgroundColor={theme.palette.colors.slate[100]}
                      variant='outlined'
                      key={sliceValue}
                      label={
                        options.find(option => option.value === sliceValue)
                          ?.label ?? ''
                      }
                      onDelete={onDeleteChip}
                      shouldStopMouseDownPropagation
                      value={sliceValue}
                      sx={{ height: '28px' }}
                    />
                  ))}
                </Box>
                <Box display='flex' alignItems='center'>
                  {chipsToShow && selected.length > chipsToShow && (
                    <Box display='block' className='hide-on-hover'>
                      <Typography variant='font14'>
                        +{selected.length - chipsToShow}
                      </Typography>
                    </Box>
                  )}
                  {onClear && <ClearButton />}
                </Box>
              </Box>
            );
          }

          return (
            <Box display='flex' alignItems='center'>
              {startAdornment}
              <Box
                overflow='hidden'
                textOverflow='ellipsis'
                width='100%'
                sx={{
                  '&:hover .show-on-hover': {
                    display: 'flex',
                  },
                }}
              >
                {selectedOption?.label || placeholder}
                {onClear && selectedOption?.label && <ClearButton />}
              </Box>
            </Box>
          );
        }}
        {...rest}
      >
        {menuTitle && (
          <MenuItem
            disabled
            sx={{
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}
          >
            <Typography variant='font16Bold'>{menuTitle}</Typography>
          </MenuItem>
        )}
        {isMenuSearchable && (
          <MenuItem
            onClickCapture={e => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onKeyDown={e => e.stopPropagation()}
            sx={{
              backgroundColor: `${theme.palette.colors.white} !important`,
              padding: '8px 16px',
            }}
          >
            <SearchBar
              size='small'
              placeholder='Search'
              value={searchText}
              onChange={({ target }) => setSearchText(target.value)}
            />
          </MenuItem>
        )}
        {menuDescription && (
          <MenuItem
            disabled
            sx={{
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}
          >
            <Typography variant='font14' color={theme.palette.colors.grey[600]}>
              {menuDescription}
            </Typography>
          </MenuItem>
        )}
        {onAddNewClick && (
          <AddNewButtonMenuItem>
            <AddNewButton onClick={onAddNewClick}>
              <IconPlus size='18px' />
              <Typography variant='font14Bold'>{addNewButtonText}</Typography>
            </AddNewButton>
          </AddNewButtonMenuItem>
        )}
        {topMenuChildren && topMenuChildren}
        {filteredAndCategorized ? (
          Object.keys(filteredAndCategorized).map(category =>
            renderOptionByCategory(category),
          )
        ) : (
          <Box display='flex' alignItems='center' justifyContent='center'>
            <NonIdealState icon={<Search />} title='No results' />
          </Box>
        )}
      </Select>
      {((error && typeof error === 'string') || helperText) && (
        <FormHelperText
          sx={{
            marginLeft: 0,
            ...(!shouldErrorMessageTakeUpSpace && {
              marginTop: 0,
              height: 0,
            }),
          }}
        >
          {error || helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export default SelectDropdown;

const AddNewButtonMenuItem = styled(MenuItem)`
  position: sticky;
  bottom: 0;
  padding: 0;
  margin-top: 8px;

  @media (min-width: 600px) {
    margin-top: 12px;
  }
`;

const AddNewButton = styled('button')`
  width: 100%;
  height: 48px;
  border: none;
  background-color: ${({ theme }) => theme.palette.colors.purple[100]};
  color: ${({ theme }) => theme.palette.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: ${({ theme }) => theme.typography.body1.fontFamily};

  :hover {
    background-color: ${({ theme }) => theme.palette.colors.purple[200]};
  }

  @media (min-width: 600px) {
    height: 40px;
  }
`;
